import { type MouseEvent, useCallback, useMemo, useState } from 'react';
import { Typography, Stack, Link, Divider, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { EventDto } from '@/types/dtos/events/response/event.dto';
import type { ContributorDto, RoleSlotDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import { useEventStore } from '@/state/stores/use-event-store.ts';
import { useSearchParams } from 'react-router';
import { ContributorInvitationCheckbox } from './contributor-invitation-checkbox';
import { getRoleSlotDescription } from '@/components/events/helpers/get-role-slot-description.helper';
import { getEntityName } from '@/components/events/helpers/get-entity-name.helper';
import { getFlowRelevantRoles, getValidEntityRoles } from '@/utils/contributor-extraction.utils';
import {
  filterRoleSlotsForInvitePublication,
  isContributorFlowRoleRowInviteable,
  isContributorSessionRoleRowInviteable,
  type InvitePublicationUiContext,
} from '@/utils/contributor-invite-publication.utils';
import { HighlightedText } from '@/components/common/highlighted-text';
import { ContributorSlotRemoteSpeakerIcon } from '@/components/events/detail/tabs/contributors/contributor-slot-remote-speaker-icon';
import {
  RemoteSpeakerDayEntitlementModal,
  useRemoteSpeakerDayEntitlementModalState,
} from '@/components/events/detail/tabs/common/contributors/remote-speaker-day-entitlement.modal';
import { RemoteSpeakerScopeModal } from '@/components/events/detail/tabs/common/contributors/remote-speaker-scope.modal';
import { resolveContributorSlotRemoteSpeakerListStatus } from '@/utils/remote-speaker-status.utils';
import { resolveFlowIdForContributorRoleSlot } from '@/utils/resolve-flow-id-for-contributor-role-slot';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { getOriginEventId } from '@/helpers/event.helper';
import { EVENT_PERMISSIONS } from '@/enums/permissions.enum';

const getEntityId = (entity: EventDto | FlowDto | SessionDto | string) => {
  if (typeof entity === 'string') {
    return entity;
  }
  return entity.id;
};

const entityTypeToTabName = {
  Event: 'dashboard',
  Flow: 'flows',
  Session: 'sessions',
};

export const ContributorRoles = ({
  contributor,
  onContributonSelected,
  searchTerm = '',
  flowContext,
  compactStatusColumn = false,
  canConfigureRemoteSpeaker = false,
}: {
  contributor: ContributorDto;
  onContributonSelected?: ({
    isSelected,
    contributorId,
    roleId,
    slotId,
  }: {
    isSelected: boolean;
    contributorId: string;
    roleId?: string;
    slotId?: string;
  }) => void;
  searchTerm?: string;
  flowContext?: FlowDto;
  /** Narrower status icon column + tighter gaps (e.g. contributor list on small screens). */
  compactStatusColumn?: boolean;
  canConfigureRemoteSpeaker?: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const event = useEventStore((s) => s.event);
  const eventForSlots = useEventStore((s) => s.eventOrganize);
  const eventSessions = useEventStore((store) => store.eventSessionsOrganize);
  const eventFlows = useEventStore((store) => store.eventFlowsOrganize);
  const eventSessionsPlan = useEventStore((store) => store.eventSessionsPlan);
  const eventFlowsPlan = useEventStore((store) => store.eventFlowsPlan);
  const eventPlan = useEventStore((store) => store.eventPlan);
  const remoteSpeakerDayEntitlementModal = useRemoteSpeakerDayEntitlementModalState();
  const [slotRemoteSpeakerEnable, setSlotRemoteSpeakerEnable] = useState<{
    roleId: string;
    slotId: string;
    flowId: string;
  } | null>(null);
  const { data: permissions } = usePermissionsQuery();
  const originEventId = event ? getOriginEventId(event) : undefined;
  const isEventManager =
    permissions?.events?.[originEventId ?? '']?.includes(EVENT_PERMISSIONS.MANAGE_EVENT_ROLES) ?? false;
  const eventForRemoteSpeakerDays = event ?? eventPlan;
  const hasOrganizeData = !!eventForSlots && !!eventFlows?.length;

  const invitePublicationCtx: InvitePublicationUiContext | undefined = useMemo(
    () =>
      eventPlan || eventFlowsPlan?.length || eventSessionsPlan?.length
        ? {
            eventPlan,
            eventFlowsPlan,
            eventSessionsPlan,
            eventFlowsOrganize: eventFlows,
            eventSessionsOrganize: eventSessions,
          }
        : undefined,
    [eventPlan, eventFlowsPlan, eventSessionsPlan, eventFlows, eventSessions],
  );

  const normalizedContributor = useMemo(() => {
    const resolveEntity = (role: ContributorDto['roles'][0]) => {
      if (!role.entity || typeof role.entity !== 'string') return role.entity;

      if (role.entityType === 'Event') {
        return eventForSlots?.id && eventForSlots.id === role.entity ? eventForSlots : role.entity;
      }

      if (role.entityType === 'Flow') {
        return eventFlows?.find((flow) => flow.id === role.entity || flow.origin === role.entity) || role.entity;
      }

      if (role.entityType === 'Session') {
        return (
          eventSessions?.find((session) => session.id === role.entity || session.origin === role.entity) || role.entity
        );
      }

      return role.entity;
    };

    return {
      ...contributor,
      roles: contributor.roles.map((role) => ({
        ...role,
        entity: resolveEntity(role),
      })),
    };
  }, [contributor, eventForSlots, eventFlows, eventSessions]);

  // Identify flow-related contributions for highlighting
  const flowRelatedContributions = useMemo(() => {
    if (!flowContext) return null;

    const flowSlotIds = new Set(flowContext.slots.map((slot) => slot.id));
    const flowRelatedRoleIds = new Set<string>();

    contributor.roles.forEach((role) => {
      const hasFlowSlots = role.slots.some((slot) => {
        if (slot.entityType !== 'FlowSlot') return false;
        const entityId = typeof slot.entity === 'string' ? slot.entity : slot.entity.id;
        return flowSlotIds.has(entityId);
      });

      if (hasFlowSlots || (role.entityType === 'Flow' && role.entity && getEntityId(role.entity) === flowContext.id)) {
        flowRelatedRoleIds.add(role.id);
      }
    });

    return { flowRelatedRoleIds };
  }, [contributor, flowContext]);

  const getSlotDescription = useCallback(
    (roleSlot: RoleSlotDto) => {
      return getRoleSlotDescription(roleSlot, eventForSlots, eventFlows, flowContext);
    },
    [eventForSlots, eventFlows, flowContext],
  );

  // Helper function to get flow for a slot
  const getFlowForSlot = useCallback(
    (slotId: string) => {
      return eventFlows?.find((flow) => flow.slots.find((slot) => slot.id === slotId)) ?? null;
    },
    [eventFlows],
  );

  const handleEntityClick = (event: MouseEvent<HTMLAnchorElement>, entityId: string, entityType: string) => {
    event.stopPropagation();
    searchParams.set('tab', entityTypeToTabName[entityType as keyof typeof entityTypeToTabName] || 'dashboard');
    if (entityType === 'Session' || entityType === 'Flow') {
      searchParams.set('key', entityId);
    }
    setSearchParams(searchParams);
  };

  const filterSlotForListGeo = useCallback(
    (slot: RoleSlotDto) => {
      if (slot.entityType === 'FlowSlot') {
        const entityId = typeof slot.entity === 'string' ? slot.entity : slot.entity.id;
        if (flowContext) {
          const matchesFlowSlot = flowContext.slots?.some((flowSlot) => flowSlot.id === entityId);
          if (!matchesFlowSlot) return false;
          if (flowContext.slot?.id && slot.eventSlotId) {
            return slot.eventSlotId === flowContext.slot.id || slot.eventSlotId === flowContext.slot.origin;
          }
          return true;
        }
        return !flowContext;
      }
      if (slot.entityType === 'Flow') {
        if (!flowContext) return true;
        if (flowContext.slot?.id && slot.eventSlotId) {
          return slot.eventSlotId === flowContext.slot.id || slot.eventSlotId === flowContext.slot.origin;
        }
        return true;
      }
      return !flowContext;
    },
    [flowContext],
  );

  // Get roles relevant to the current flow context
  const relevantRolesAll = useMemo(() => {
    if (!flowContext) {
      // Not in flow context, show all roles with valid entity names
      const validRoles = getValidEntityRoles(normalizedContributor);
      return validRoles;
    }

    // In flow context, use the utility function to filter roles
    return getFlowRelevantRoles(normalizedContributor, flowContext, event?.multiFlow, getEntityName);
  }, [flowContext, event?.multiFlow, normalizedContributor]);

  const pendingFlowRolesCount = useMemo(() => {
    if (!eventFlowsPlan?.length) return 0;
    return relevantRolesAll.filter(
      (role) => role.entityType === 'Flow' && !isContributorFlowRoleRowInviteable(role, eventFlowsPlan, eventFlows),
    ).length;
  }, [relevantRolesAll, eventFlowsPlan, eventFlows]);

  const relevantRoles = useMemo(() => {
    return relevantRolesAll.filter((role) => {
      if (role.entityType === 'Event') return true;
      if (role.entityType === 'Flow') {
        if (!eventFlowsPlan?.length) return true;
        return isContributorFlowRoleRowInviteable(role, eventFlowsPlan, eventFlows);
      }
      if (role.entityType === 'Session') {
        if (!eventSessionsPlan?.length) return true;
        return isContributorSessionRoleRowInviteable(role, eventSessionsPlan, eventSessions);
      }
      return true;
    });
  }, [relevantRolesAll, eventFlowsPlan, eventSessionsPlan, eventFlows, eventSessions]);

  /** Match contributor-row status column; minHeight pairs with the role title row for top-aligned rows */
  const statusIconColumnSx = {
    width: compactStatusColumn ? 48 : 44,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 24,
    boxSizing: 'border-box',
  };

  const slotStatusIconColumnSx = {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0.25,
    minHeight: 24,
    boxSizing: 'border-box',
    width: 'fit-content',
  };

  return (
    <Stack
      spacing={compactStatusColumn ? { xs: 1.25, sm: 1.25 } : { xs: 2, sm: 1.5 }}
      direction="column"
      sx={{ width: '100%' }}
    >
      {relevantRoles.length > 0 ? (
        relevantRoles
          .sort((a, b) => {
            // Define order: Event -> Flow -> Session
            const order = { Event: 0, Flow: 1, Session: 2 };
            return (order[a.entityType as keyof typeof order] || 0) - (order[b.entityType as keyof typeof order] || 0);
          })
          .flatMap((role, index, sortedRoles) => {
            const elements = [];

            // Add divider if this is the first role of a new entity type
            if (index > 0 && role.entityType !== sortedRoles[index - 1].entityType) {
              elements.push(<Divider key={`divider-${role.entityType}-${index}`} sx={{ my: { xs: 0.5, sm: 0.75 } }} />);
            }

            const isLastOfType =
              index === sortedRoles.length - 1 || sortedRoles[index + 1].entityType !== role.entityType;

            // Add the role element
            if (!role.entity) return elements;
            const entityIdStrict = getEntityId(role.entity);
            const entityName = getEntityName(role);

            const sessionForRole =
              role.entityType === 'Session'
                ? typeof role.entity === 'string'
                  ? eventSessions?.find((s) => s.id === role.entity)
                  : eventSessions?.find((s) => s.id === entityIdStrict)
                : undefined;

            const roleElement = (
              <Stack
                direction="row"
                spacing={compactStatusColumn ? 0.75 : 1}
                key={`role-${index}`}
                alignItems="flex-start"
                sx={{ width: '100%' }}
              >
                <Box
                  sx={{
                    ...statusIconColumnSx,
                    // Keep the role icon on the title row; do not center against title + all slots
                    alignSelf: 'flex-start',
                  }}
                >
                  <ContributorInvitationCheckbox
                    isInvited={!!role.invitedAt}
                    isAccepted={!!role.acceptedAt}
                    isSelected={role.stagedForInvite === true}
                    contributorEmail={contributor.email}
                    isDisabled={!contributor.email}
                    onSelect={(isSelected) =>
                      onContributonSelected?.({ isSelected, contributorId: contributor.id, roleId: role.id })
                    }
                  />
                </Box>
                <Stack sx={{ minWidth: 0, flex: 1 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ minHeight: 24, boxSizing: 'border-box' }}
                  >
                    {(role.entityType === 'Session' && !sessionForRole?.isInLibrary) || flowContext ? (
                      <HighlightedText
                        text={
                          role.entityType === 'Event'
                            ? `${t(`common.roleLabels.${role.role}`)}`
                            : `${entityName} (${t(`common.roleLabels.${role.role}`)})`
                        }
                        searchTerm={searchTerm}
                        variant="subtitle2"
                        component="div"
                        color={
                          !flowContext && flowRelatedContributions?.flowRelatedRoleIds?.has(role.id)
                            ? 'secondary.main'
                            : 'text.primary'
                        }
                        sx={{
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          maxWidth: '100%',
                          fontWeight: role.entityType === 'Event' ? 700 : 600,
                          lineHeight: 1.35,
                        }}
                      />
                    ) : (
                      <Link
                        onClick={(event) => handleEntityClick(event, entityIdStrict, role.entityType)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <HighlightedText
                          text={
                            role.entityType === 'Event'
                              ? `${t(`common.roleLabels.${role.role}`)}`
                              : `${entityName} (${t(`common.roleLabels.${role.role}`)})`
                          }
                          searchTerm={searchTerm}
                          variant="subtitle2"
                          component="div"
                          color={
                            !flowContext && flowRelatedContributions?.flowRelatedRoleIds?.has(role.id)
                              ? 'secondary.main'
                              : 'text.primary'
                          }
                          sx={{
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '100%',
                            fontWeight: role.entityType === 'Event' ? 700 : 600,
                            lineHeight: 1.35,
                          }}
                        />
                      </Link>
                    )}
                  </Stack>
                  <Stack
                    direction="column"
                    alignItems="flex-start"
                    spacing={0.75}
                    sx={{
                      mt: { xs: 1, sm: 0.75 },
                      pl: { xs: 0, sm: 1.25 },
                      ml: { xs: 0, sm: 0.25 },
                      borderLeft: { xs: 'none', sm: '2px solid' },
                      borderColor: 'divider',
                    }}
                  >
                    <>
                      {(() => {
                        const geoSlots =
                          role.slots?.filter((slot) => {
                            if (slot.deleted === true) return false;
                            if (slot.entityType === 'FlowSlot') {
                              const entityId = typeof slot.entity === 'string' ? slot.entity : slot.entity.id;
                              if (!flowContext) {
                                const flowForSlot = getFlowForSlot(entityId);
                                return !!(flowForSlot && flowForSlot.title);
                              }
                              return filterSlotForListGeo(slot);
                            }
                            return filterSlotForListGeo(slot);
                          }) ?? [];
                        const slotsForList = invitePublicationCtx
                          ? filterRoleSlotsForInvitePublication(role, geoSlots, invitePublicationCtx)
                          : geoSlots;
                        const hiddenByOrganize = invitePublicationCtx
                          ? Math.max(0, geoSlots.length - slotsForList.length)
                          : 0;

                        return hasOrganizeData ? (
                          <>
                            {slotsForList.map((slot) => {
                              const description = getSlotDescription(slot);
                              if (!description) return null;

                              const slotRemoteStatus = resolveContributorSlotRemoteSpeakerListStatus({
                                contributor,
                                role,
                                roleSlot: slot,
                                event: eventForRemoteSpeakerDays ?? undefined,
                              });
                              const flowIdForSlot = resolveFlowIdForContributorRoleSlot(role, slot, eventFlows);
                              const onEnableSlotRemoteSpeaker =
                                canConfigureRemoteSpeaker && slotRemoteStatus.openEnableModalOnClick && flowIdForSlot
                                  ? () => {
                                      setSlotRemoteSpeakerEnable({
                                        roleId: role.id,
                                        slotId: slot.id,
                                        flowId: flowIdForSlot,
                                      });
                                    }
                                  : undefined;
                              const onPendingDayClick =
                                canConfigureRemoteSpeaker &&
                                slotRemoteStatus.dayStatus === 'pending' &&
                                slotRemoteStatus.dayId
                                  ? () => {
                                      remoteSpeakerDayEntitlementModal.openForContributorDay(
                                        contributor,
                                        slotRemoteStatus.dayId,
                                        eventPlan ?? eventForSlots,
                                      );
                                    }
                                  : undefined;

                              return (
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={compactStatusColumn ? 0.75 : 1}
                                  key={slot.id}
                                  sx={{ width: '100%' }}
                                >
                                  <Box sx={slotStatusIconColumnSx}>
                                    <ContributorInvitationCheckbox
                                      isInvited={!!slot.invitedAt}
                                      isAccepted={!!slot.acceptedAt}
                                      isSelected={slot.stagedForInvite === true}
                                      contributorEmail={contributor.email}
                                      isDisabled={!contributor.email}
                                      onSelect={(isSelected) =>
                                        onContributonSelected?.({
                                          isSelected,
                                          contributorId: contributor.id,
                                          roleId: role.id,
                                          slotId: slot.id,
                                        })
                                      }
                                    />
                                    <ContributorSlotRemoteSpeakerIcon
                                      t={t}
                                      showIcon={canConfigureRemoteSpeaker && slotRemoteStatus.showIcon}
                                      slotIsRemote={slotRemoteStatus.slotIsRemote}
                                      iconColor={slotRemoteStatus.color ?? 'info'}
                                      dayStatus={slotRemoteStatus.dayStatus}
                                      onEnableRemoteSpeaker={onEnableSlotRemoteSpeaker}
                                      onPendingDayClick={onPendingDayClick}
                                    />
                                  </Box>
                                  <HighlightedText
                                    text={description}
                                    searchTerm={searchTerm}
                                    variant="body2"
                                    color="text.primary"
                                    sx={{
                                      flex: 1,
                                      minWidth: 0,
                                      fontWeight: 400,
                                      wordBreak: 'break-word',
                                      overflowWrap: 'break-word',
                                      lineHeight: 1.45,
                                      opacity: 0.88,
                                    }}
                                  />
                                </Stack>
                              );
                            })}

                            {hiddenByOrganize > 0 ? (
                              <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ fontStyle: 'italic', mt: 0, opacity: 0.8 }}
                              >
                                {t('event.detail.contributors.slotsPendingPublication', { count: hiddenByOrganize })}
                              </Typography>
                            ) : null}
                          </>
                        ) : null;
                      })()}
                    </>
                  </Stack>
                </Stack>
              </Stack>
            );

            elements.push(roleElement);

            if (isLastOfType && role.entityType === 'Flow' && pendingFlowRolesCount > 0) {
              elements.push(
                <Typography
                  key={`pending-flow-${index}`}
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: 'italic', mt: 0 }}
                >
                  {t('event.detail.contributors.flowsPendingPublication', { count: pendingFlowRolesCount })}
                </Typography>,
              );
            }

            return elements;
          })
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            minHeight: '32px',
            display: 'flex',
            alignItems: 'flex-start',
          }}
        >
          {pendingFlowRolesCount > 0
            ? t('event.detail.contributors.flowsPendingPublication', { count: pendingFlowRolesCount })
            : t('event.detail.contributors.noRoles')}
        </Typography>
      )}
      {remoteSpeakerDayEntitlementModal.contributor ? (
        <RemoteSpeakerDayEntitlementModal
          open={remoteSpeakerDayEntitlementModal.open}
          onClose={remoteSpeakerDayEntitlementModal.close}
          contributor={remoteSpeakerDayEntitlementModal.contributor}
          dayIds={remoteSpeakerDayEntitlementModal.dayIds}
          dayLabel={remoteSpeakerDayEntitlementModal.dayLabel}
          isEventManager={isEventManager}
        />
      ) : null}
      {slotRemoteSpeakerEnable ? (
        <RemoteSpeakerScopeModal
          open
          onClose={() => setSlotRemoteSpeakerEnable(null)}
          contributor={contributor}
          enabled
          flowId={slotRemoteSpeakerEnable.flowId}
          roleId={slotRemoteSpeakerEnable.roleId}
          slotId={slotRemoteSpeakerEnable.slotId}
          showSlotOption
        />
      ) : null}
    </Stack>
  );
};
